import type { FormEvent } from 'react'
import type { ModuleRow } from '~/pages/backoffice/admin/modules/ModulesList'
import { useMutation } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { GQL_RENAME_SCORM_MODULE } from '~/gql/scorm-modules/scorm-modules'

/** Longueur maximale d'un titre de module, comme côté API (`MAX_MODULE_TITLE`). */
const MAX_MODULE_TITLE = 200

type Props = {
  module: Pick<ModuleRow, 'id' | 'title'>
  onClose: () => void
}

/**
 * Renommer un module déjà importé : le titre change partout où il se lit
 * (parcours, espace apprenant, rapports), le suivi ne bouge pas.
 */
function ModuleRenameDialog({ module, onClose }: Props) {
  const { t } = useTranslation()
  const [title, setTitle] = useState(module.title)
  const [rename, { loading }] = useMutation(GQL_RENAME_SCORM_MODULE)
  const trimmed = title.trim()

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!trimmed) return
    try {
      await rename({ variables: { id: module.id, title: trimmed } })
      toast.success(t('modules.rename.done'))
      onClose()
    } catch {
      toast.error(t('modules.rename.failed'))
    }
  }

  return (
    <Dialog open onOpenChange={next => !next && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t('modules.rename.title')}</DialogTitle>
            <DialogDescription>{t('modules.rename.body')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="module-rename-title">{t('common.title')}</label>
            <Input
              id="module-rename-title"
              maxLength={MAX_MODULE_TITLE}
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={loading || !trimmed || trimmed === module.title}>{t('common.save')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ModuleRenameDialog
